import React, { useState, useEffect } from 'react';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import HubIcon from '@mui/icons-material/Hub';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SaveIcon from '@mui/icons-material/Save';
import api from '../../services/api';

const RoleConfiguration = ({ role, onBack }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const modules = [
    { id: 'Risk Registers', name: 'Risk Registers', icon: <ShieldIcon />, color: '#1e40af' }, // Royal Blue
    { id: 'Risk Assessment', name: 'Risk Assessment', icon: <AssessmentIcon />, color: '#1e3a8a' }, // Deep Navy
    { id: 'Controls & Treatment', name: 'Controls & Treatment', icon: <SecurityIcon />, color: '#10b981' }, // Emerald
    { id: 'SHE Compliance', name: 'SHE Compliance', icon: <CheckCircleIcon />, color: '#059669' }, // Forest Green
    { id: 'Loss Events', name: 'Loss Events', icon: <BarChartIcon />, color: '#334155' }, // Slate
    { id: 'Monte Carlo Analysis', name: 'Monte Carlo Analysis', icon: <InsightsIcon />, color: '#1e293b' }, // Midnight
    { id: 'Business Continuity', name: 'Business Continuity', icon: <HubIcon />, color: '#0f172a' }, // Dark Slate
    { id: 'Analysis & Reports', name: 'Analysis & Reports', icon: <BarChartIcon />, color: '#475569' }, // Cool Grey
    { id: 'User Management', name: 'User Management', icon: <PeopleIcon />, color: '#000000' }, // Black
    { id: 'Sessions & Logs', name: 'Sessions & Logs', icon: <AdminPanelSettingsIcon />, color: '#4b5563' }, // Charcoal
  ];

  useEffect(() => {
    fetchPermissions();
  }, [role.role_id]);

  const getSystemDefaults = (roleName) => {
    // Align EXACTLY with Sidebar.js roles
    const riskRoles = ['risk_owner', 'risk_champion', 'risk_admin', 'manager', 'director', 'Risk Owner', 'Risk Champion', 'Risk Administrator', 'Manager', 'Director'];
    const sheRoles = ['she_officer', 'she_manager', 'SHE Officer', 'SHE Manager'];
    const allBizRoles = [...riskRoles, ...sheRoles];
    const adminRoles = ['admin', 'sys_admin', 'System Administrator', 'System Admin'];

    const defaults = {};
    const normalizedRole = roleName.trim().toLowerCase();

    // Special case: Sys Admin sees everything by default (matching Sidebar.js fallback)
    const isSysAdmin = adminRoles.some(r => r.toLowerCase() === normalizedRole);

    const mapping = {
      'Risk Registers': riskRoles,
      'Risk Assessment': riskRoles,
      'Controls & Treatment': riskRoles,
      'SHE Compliance': allBizRoles,
      'Loss Events': allBizRoles,
      'Monte Carlo Analysis': allBizRoles,
      'Business Continuity': riskRoles,
      'Analysis & Reports': allBizRoles,
      'User Management': adminRoles,
      'Sessions & Logs': adminRoles
    };

    Object.keys(mapping).forEach(mod => {
      const isAllowed = isSysAdmin || mapping[mod].some(r => r.toLowerCase() === normalizedRole);
      defaults[mod] = { view: isAllowed, add: isAllowed, edit: isAllowed, delete: false };
    });

    return defaults;
  };

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const defaults = getSystemDefaults(role.name);
      console.log('Fetching permissions for:', role.id || role.role_id);
      const resp = await api.get(`/permissions/${role.id || role.role_id}`);
      const data = resp.data;
      
      // CRITICAL: Merge DB results over system defaults
      const mapped = { ...defaults };
      if (data && data.length > 0) {
        data.forEach(p => {
          mapped[p.section_name] = { 
            view: parseInt(p.can_view) === 1, 
            add: parseInt(p.can_add) === 1, 
            edit: parseInt(p.can_edit) === 1, 
            delete: parseInt(p.can_delete) === 1 
          };
        });
      }
      console.log('Final Permission Map:', mapped);
      setPermissions(mapped);
    } catch (e) {
      console.error('Fetch error:', e);
      setMessage({ type: 'error', text: 'Connection failed. Showing system defaults.' });
      setPermissions(getSystemDefaults(role.name));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (currentPerms) => {
    setSaving(true);
    try {
      await api.post(`/permissions/${role.id || role.role_id}`, { 
        permissions: currentPerms, 
        entity_type: 'role' 
      });
      setMessage({ type: 'success', text: 'Changes synchronized.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (e) {
      setMessage({ type: 'error', text: 'Save failed. Check connection.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (moduleId, type) => {
    const newState = {
      ...permissions,
      [moduleId]: {
        ...(permissions[moduleId] || { view: false, add: false, edit: false, delete: false }),
        [type]: !permissions[moduleId]?.[type]
      }
    };
    setPermissions(newState);
    // Auto-save in background
    handleSave(newState);
  };

  if (loading) return <div className="animate-pulse" style={{ padding: '100px', textAlign: 'center', color: 'var(--primary)', fontWeight: 600 }}>Analyzing System Protocols...</div>;

  return (
    <div className="animate-fade-in" style={{ paddingBottom: '100px' }}>
      <header style={{ marginBottom: '40px', display: 'flex', alignItems: 'center', gap: '24px' }}>
        <button className="btn-badge" style={{ padding: '12px', background: 'rgba(255,255,255,0.05)' }} onClick={onBack}>
          <ArrowBackIcon />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
            <h2 className="gradient-text" style={{ fontSize: '32px' }}>{role.name.replace('_', ' ')}: Access Console</h2>
            {saving && (
              <div style={{ 
                background: 'rgba(30, 64, 175, 0.1)', 
                color: '#3b82f6', 
                fontSize: '11px', 
                fontWeight: 700, 
                padding: '4px 10px', 
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <div className="animate-spin" style={{ width: '10px', height: '10px', border: '2px solid transparent', borderTopColor: 'currentColor', borderRadius: '50%' }} />
                SAVING...
              </div>
            )}
            {message.type === 'success' && !saving && (
              <div style={{ color: '#10b981', fontSize: '11px', fontWeight: 700 }}>✓ DATA PERSISTED</div>
            )}
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', letterSpacing: '0.5px' }}>Configure default module visibility and authority levels for this functional role.</p>
        </div>
        <button 
          className="btn" 
          style={{ 
            marginLeft: 'auto', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            padding: '0 40px',
            height: '56px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, var(--primary) 0%, #3b82f6 100%)',
            color: 'white',
            fontWeight: 700,
            fontSize: '15px',
            border: 'none',
            boxShadow: '0 10px 20px -5px rgba(59, 130, 246, 0.4)',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
          onClick={() => handleSave(permissions)}
          disabled={saving}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 15px 30px -10px rgba(59, 130, 246, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 20px -5px rgba(59, 130, 246, 0.4)';
          }}
        >
          {saving ? 'Synchronizing...' : <><SaveIcon /> Save Changes</>}
        </button>
      </header>

      {message.type === 'error' && message.text && (
        <div className={`card glass animate-slide-up`} style={{
          padding: '16px',
          marginBottom: '32px',
          border: '1px solid #ef4444',
          background: 'rgba(239, 68, 68, 0.05)',
          color: '#ef4444',
          fontWeight: 600,
          textAlign: 'center'
        }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 350px), 1fr))', gap: '24px' }}>
        {modules.map(mod => {
          const isEnabled = permissions[mod.id]?.view;
          return (
            <div key={mod.id} className="card glass animate-slide-up" style={{
              padding: '24px',
              border: `1px solid ${isEnabled ? 'rgba(30, 64, 175, 0.3)' : 'var(--border-color)'}`,
              background: isEnabled ? 'rgba(30, 64, 175, 0.02)' : 'transparent',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  background: isEnabled ? mod.color : 'rgba(255,255,255,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isEnabled ? 'white' : 'var(--text-muted)',
                  transition: 'all 0.3s'
                }}>
                  {mod.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '15px', color: isEnabled ? 'white' : 'var(--text-muted)' }}>{mod.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', letterSpacing: '0.3px' }}>{isEnabled ? 'Full Protocol Active' : 'Access Restricted'}</div>
                </div>
                <div
                  onClick={() => handleToggle(mod.id, 'view')}
                  style={{
                    width: '44px',
                    height: '24px',
                    background: isEnabled ? '#10b981' : 'rgba(255,255,255,0.1)',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.3s'
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '3px',
                    left: isEnabled ? '23px' : '3px',
                    width: '18px',
                    height: '18px',
                    background: 'white',
                    borderRadius: '50%',
                    transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                  }} />
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                opacity: isEnabled ? 1 : 0.3,
                pointerEvents: isEnabled ? 'auto' : 'none',
                transition: 'all 0.3s'
              }}>
                {['add', 'edit', 'delete'].map(action => (
                  <button
                    key={action}
                    onClick={() => handleToggle(mod.id, action)}
                    style={{
                      padding: '8px 4px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      background: permissions[mod.id]?.[action] ? '#1e40af22' : 'transparent',
                      color: permissions[mod.id]?.[action] ? '#60a5fa' : 'var(--text-muted)',
                      fontSize: '10px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {action}
                  </button>
                ))}
              </div>

              {isEnabled && (
                <div style={{
                  marginTop: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#10b981',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  AUTHORIZED
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RoleConfiguration;
